// src/features/invoice/components/InvoicePreview.jsx
import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Box,
  Typography,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  LinearProgress,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { Print, Close, Send, Payment, PictureAsPdf } from "@mui/icons-material";
import { formatINR } from "../utils/invoiceHelpers";
import InvoiceStatusBadge from "./InvoiceStatusBadge";
import { downloadPDF, printElement } from "../utils/pdfGenerator";

const InvoicePreview = ({
  open,
  onClose,
  invoice,
  onSend,
  onRecordPayment,
}) => {
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const invoiceRef = useRef(null);

  if (!invoice) return null;

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isOverdue = () => {
    return new Date(invoice.dueDate) < new Date() && invoice.status !== "paid";
  };

  const getBalanceDue = () => {
    return (invoice.total || 0) - (invoice.amountPaid || 0);
  };

  const handlePaymentSubmit = () => {
    const amount = parseFloat(paymentAmount);
    if (amount > 0 && amount <= getBalanceDue()) {
      onRecordPayment && onRecordPayment(invoice.id, { amount });
      setPaymentDialogOpen(false);
      setPaymentAmount("");
    }
  };

  // src/features/invoice/components/InvoicePreview.jsx
  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) {
      setSnackbarMessage("Invoice content not ready. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setDownloading(true);
    try {
      const filename = `Invoice_${invoice.number}_${invoice.clientName}.pdf`;
      const result = await downloadPDF(invoiceRef.current, {
        filename,
        margin: 15,
        scale: 3,
      });

      if (result.success) {
        setSnackbarMessage("PDF downloaded successfully!");
        setSnackbarSeverity("success");
      } else {
        setSnackbarMessage("Failed to download PDF. Please try again.");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("PDF download failed:", error);
      setSnackbarMessage("Failed to download PDF. Please try again.");
      setSnackbarSeverity("error");
    } finally {
      setDownloading(false);
      setSnackbarOpen(true);
    }
  };

  const handlePrint = () => {
    if (invoiceRef.current) {
      printElement(invoiceRef.current);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Invoice #{invoice.number}</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton onClick={handlePrint} size="small" title="Print">
                <Print />
              </IconButton>
              <IconButton
                onClick={handleDownloadPDF}
                size="small"
                title="Download PDF"
                disabled={downloading}
              >
                {downloading ? (
                  <CircularProgress size={20} />
                ) : (
                  <PictureAsPdf />
                )}
              </IconButton>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            ref={invoiceRef}
            id="invoice-content"
            sx={{
              bgcolor: "#ffffff",
              p: 4,
              maxWidth: "210mm",
              margin: "0 auto",
              fontFamily: "Arial, sans-serif",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 4,
                borderBottom: "2px solid #667eea",
                pb: 2,
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Hashtag Photography Studio
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Main St, Mettu Theru, Thanjavur, Tamil Nadu 613009
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  hashtagphotographymail@gmail.com
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Phone: +91 86676 59249
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  INVOICE
                </Typography>
                <Typography variant="body2">#{invoice.number}</Typography>
                <Box sx={{ mt: 1 }}>
                  <InvoiceStatusBadge status={invoice.status} />
                </Box>
                {isOverdue() && (
                  <Chip
                    label="OVERDUE"
                    color="error"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Box>

            {/* Client Info */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Bill To:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {invoice.clientName}
                </Typography>
                <Typography variant="body2">{invoice.clientEmail}</Typography>
                {invoice.clientPhone && (
                  <Typography variant="body2">{invoice.clientPhone}</Typography>
                )}
                {invoice.clientCompany && (
                  <Typography variant="body2" color="text.secondary">
                    {invoice.clientCompany}
                  </Typography>
                )}
                {invoice.clientAddress && (
                  <Typography variant="body2" color="text.secondary">
                    {invoice.clientAddress.street}
                    <br />
                    {invoice.clientAddress.city}, {invoice.clientAddress.state}{" "}
                    {invoice.clientAddress.zipCode}
                    <br />
                    {invoice.clientAddress.country}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Invoice Details:
                </Typography>
                <Typography variant="body2">
                  Date: {formatDate(invoice.date)}
                </Typography>
                <Typography variant="body2">
                  Due Date: {formatDate(invoice.dueDate)}
                </Typography>
                {invoice.paymentTerms && (
                  <Typography variant="body2">
                    Terms: {invoice.paymentTerms}
                  </Typography>
                )}
                <Typography variant="body2">
                  Currency: {invoice.currency}
                </Typography>
                {invoice.purchaseOrderNumber && (
                  <Typography variant="body2">
                    PO#: {invoice.purchaseOrderNumber}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {/* Items Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {item.description}
                        {item.notes && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Note: {item.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {formatINR(item.unitPrice)}
                      </TableCell>
                      <TableCell align="right">
                        {item.discount ? `${item.discount}%` : "-"}
                      </TableCell>
                      <TableCell align="right" fontWeight="bold">
                        {formatINR(
                          item.total || item.quantity * item.unitPrice,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Box sx={{ width: 300 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">
                    {formatINR(invoice.subtotal)}
                  </Typography>
                </Box>
                {invoice.discount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="error">
                      Discount (
                      {invoice.discountType === "percentage"
                        ? `${invoice.discount}%`
                        : "Fixed"}
                      ):
                    </Typography>
                    <Typography variant="body2" color="error">
                      -{formatINR(invoice.discountAmount)}
                    </Typography>
                  </Box>
                )}
                {invoice.taxRate > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">
                      Tax ({invoice.taxRate}%):
                    </Typography>
                    <Typography variant="body2">
                      {formatINR(invoice.taxAmount)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6" fontWeight="bold">
                    Total:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {formatINR(invoice.total)}
                  </Typography>
                </Box>
                {invoice.amountPaid > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2">Amount Paid:</Typography>
                    <Typography variant="body2" color="success.main">
                      {formatINR(invoice.amountPaid)}
                    </Typography>
                  </Box>
                )}
                {getBalanceDue() > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Balance Due:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      {formatINR(getBalanceDue())}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Notes & Terms */}
            {invoice.notes && (
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Notes:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {invoice.notes}
                </Typography>
              </Box>
            )}
            {invoice.termsAndConditions && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Terms & Conditions:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {invoice.termsAndConditions}
                </Typography>
              </Box>
            )}

            {/* Footer */}
            <Box
              sx={{
                mt: 4,
                pt: 2,
                borderTop: "1px solid #e0e0e0",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                This is a computer-generated invoice. Please verify all details.
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                Payment due by {formatDate(invoice.dueDate)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {/* {invoice.status === "draft" && onSend && (
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={() => onSend(invoice.id)}
            >
              Send to Client
            </Button>
          )} */}
          {getBalanceDue() > 0 &&
            invoice.status !== "paid" &&
            onRecordPayment && (
              <Button
                variant="contained"
                startIcon={<Payment />}
                onClick={() => setPaymentDialogOpen(true)}
              >
                Record Payment
              </Button>
            )}
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={handleDownloadPDF}
            disabled={downloading}
          >
            {downloading ? <CircularProgress size={20} /> : "Download PDF"}
          </Button>
        </DialogActions>

        {/* Payment Dialog */}
        <Dialog
          open={paymentDialogOpen}
          onClose={() => setPaymentDialogOpen(false)}
        >
          <DialogTitle>Record Payment</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Balance Due: {formatINR(getBalanceDue())}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Payment Amount"
              type="number"
              fullWidth
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              InputProps={{ startAdornment: <span>₹</span> }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handlePaymentSubmit}
              variant="contained"
              disabled={
                !paymentAmount ||
                parseFloat(paymentAmount) <= 0 ||
                parseFloat(paymentAmount) > getBalanceDue()
              }
            >
              Record Payment
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InvoicePreview;
